<script setup lang="ts">
import { computed } from "vue";

const props = defineProps<{
  isGenerating?: boolean;
  modulesCount?: number;
  showPulse?: boolean;
}>();

defineEmits<{
  (e: "click"): void;
}>();

const showBadge = computed(
  () => (props.modulesCount ?? 0) > 0 && !props.isGenerating,
);
</script>

<template>
  <button
    class="ai-fab-button"
    :class="{
      'is-generating': isGenerating,
      'has-pulse': showPulse && !isGenerating,
    }"
    @click="$emit('click')"
  >
    <!-- Wave ripples when generating -->
    <template v-if="isGenerating">
      <span class="wave-ring"></span>
      <span class="wave-ring delay-1"></span>
      <span class="wave-ring delay-2"></span>
    </template>

    <!-- Icon with blink effect when generating -->
    <i class="bi-stars" :class="{ 'icon-blink': isGenerating }" />

    <span v-if="showBadge" class="badge">
      {{ modulesCount }}
    </span>
  </button>
</template>

<style scoped lang="scss">
.ai-fab-button {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
  z-index: 1000;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(139, 92, 246, 0.5);
  }

  &:active {
    transform: scale(0.98);
  }

  &.has-pulse {
    animation: pulse 2s infinite;
  }

  &.is-generating {
    cursor: pointer;

    &:hover {
      transform: scale(1.05);
    }
  }

  .badge {
    position: absolute;
    top: -4px;
    right: -4px;
    background: var(--el-color-danger);
    border-radius: 50%;
    width: 20px;
    height: 20px;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }

  // Icon blink animation when generating
  .icon-blink {
    animation: iconBlink 1.5s ease-in-out infinite;
    z-index: 1;
  }
}

// Wave ripple rings
.wave-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid rgba(139, 92, 246, 0.6);
  animation: waveExpand 2s ease-out infinite;
  pointer-events: none;

  &.delay-1 {
    animation-delay: 0.6s;
  }

  &.delay-2 {
    animation-delay: 1.2s;
  }
}

@keyframes waveExpand {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(2.2);
    opacity: 0;
  }
}

@keyframes iconBlink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
  }
  50% {
    box-shadow:
      0 4px 20px rgba(139, 92, 246, 0.7),
      0 0 0 8px rgba(139, 92, 246, 0.15);
  }
  100% {
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
  }
}
</style>
